/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useContext, useCallback } from 'react';
import { ExternalLink } from '@manulife/mux-cds-icons';
import PropTypes from 'prop-types';
import i18n from '../../lang/i18n/index.js';
import { safeRest, rem, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import { CONTENT_TYPE } from './constants.js';
import SideNavContext from './SideNavContext.js';
import { SubItemComponent, SubItemLabelWrapper } from './styledComponents.js';
import { getNavItemProps } from './utils.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';
import useLang from '../../hooks/useLang.js';

var _excluded = ["id", "as", "label", "itemKey", "contentType", "to", "isExternal", "onClick", "children"];
var SubNavItem = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var controlledId = _ref.id,
    _ref$as = _ref.as,
    as = _ref$as === undefined ? 'a' : _ref$as,
    label = _ref.label,
    itemKey = _ref.itemKey,
    contentType = _ref.contentType,
    to = _ref.to,
    _ref$isExternal = _ref.isExternal,
    isExternal = _ref$isExternal === undefined ? false : _ref$isExternal,
    _ref$onClick = _ref.onClick,
    onClick = _ref$onClick === undefined ? noop : _ref$onClick,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId(), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var lang = useLang();
  var _useContext = useContext(SideNavContext),
    keepOpenOnSelect = _useContext.keepOpenOnSelect,
    activeKey = _useContext.activeKey,
    expandedParentKey = _useContext.expandedParentKey,
    handleNavItemClick = _useContext.handleNavItemClick,
    setActiveParentKey = _useContext.setActiveParentKey,
    setExpandedParentKey = _useContext.setExpandedParentKey;
  var isActive = activeKey === itemKey;
  var subNavItemProps = getNavItemProps({
    component: as,
    to: to,
    isExternal: isExternal
  });
  var handleClick = useCallback(function (e) {
    handleNavItemClick(itemKey);
    if (!keepOpenOnSelect) {
      setExpandedParentKey();
    }
    setActiveParentKey(expandedParentKey);
    handle(onClick)(e);
  }, [handleNavItemClick, setActiveParentKey, setExpandedParentKey, keepOpenOnSelect, itemKey, expandedParentKey, onClick]);
  return /*#__PURE__*/jsx("li", {
    children: /*#__PURE__*/jsxs(SubItemComponent, _objectSpread(_objectSpread(_objectSpread({}, subNavItemProps), {}, {
      id: id,
      as: as,
      "aria-current": isActive ? 'page' : false,
      "data-selected": isActive,
      "data-testid": "sub-item-".concat(id),
      onClick: handleClick,
      theme: theme,
      ref: ref,
      $contentType: contentType,
      $isActive: isActive
    }, safeProps), {}, {
      children: [/*#__PURE__*/jsxs(SubItemLabelWrapper, {
        children: [label, isExternal && /*#__PURE__*/jsx(ExternalLink, {
          title: "external-icon",
          fontSize: rem(14),
          style: {
            marginLeft: rem(4)
          },
          "aria-label": i18n[lang].sideNav.externalLinkText
        })]
      }), contentType === CONTENT_TYPE.BANNER && children]
    }))
  });
});
process.env.NODE_ENV !== "production" ? SubNavItem.propTypes = {
  /** id for SubNavItem */
  id: PropTypes.string,
  /** type of component to use as nav item (e.g. Link from react-router-dom, button, etc.) */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** label for SubNavItem */
  label: PropTypes.node,
  /** unique key used for item selection */
  itemKey: PropTypes.string.isRequired,
  /** type of content displayed in the sub nav */
  contentType: PropTypes.oneOf([CONTENT_TYPE.BANNER, CONTENT_TYPE.DROPDOWN]),
  /** url (by default maps to href) or url/object/function (when using Link from react-router-dom) */
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),
  /** flag to indicate an external link that will open in a new tab */
  isExternal: PropTypes.bool,
  /** onClick handler for item */
  onClick: PropTypes.func,
  /** content inside banner when `contentType={CONTENT_TYPE.BANNER}` */
  children: PropTypes.node
} : undefined;
process.env.NODE_ENV !== "production" ? SubNavItem.displayName = 'SubNavItem' : undefined;

export { SubNavItem as default };
